/**
 * Object Editor Card Component
 *
 * Form-based editor for a single IDF object with rich field metadata.
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { FieldEditor } from './FieldEditors';
import { useIDFStore } from '../stores/idfStore';
import { useIDDStore } from '../stores/iddStore';
import { useDocumentation } from '@/docs';
import type { IDFObject, IDFField } from '../parser/types';
import type { IDDObjectType, IDDField } from '../idd/types';
import type { ObjectValidation, FieldValidation, ValidationStatus } from './types';
import { validateObject, findReferencingObjects } from './validation';

// Book icon for documentation
function BookOpenIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

/**
 * Validation status badge
 */
function StatusBadge({ status }: { status: ValidationStatus }) {
  switch (status) {
    case 'valid':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-400">
          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
          Valid
        </span>
      );
    case 'warning':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
          </svg>
          {/* Count: {validation.warnings.length} */}
          Warning
        </span>
      );
    case 'error':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-400">
          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          Error
        </span>
      );
  }
}

/**
 * Card header with object class and name
 */
function CardHeader({
  object,
  classInfo,
  validation,
  onDuplicate,
  onDelete,
  onOpenDocs,
  expanded,
  onToggleExpand,
}: {
  object: IDFObject;
  classInfo: IDDObjectType | undefined;
  validation: ObjectValidation;
  onDuplicate: () => void;
  onDelete: () => void;
  onOpenDocs: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const objectName = object.fields[0]?.value || `${object.className} (unnamed)`;

  return (
    <div className="flex items-start justify-between border-b bg-muted px-4 py-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <button
            onClick={onToggleExpand}
            className="rounded p-0.5 text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          >
            <svg
              className={`h-5 w-5 transition-transform ${expanded ? 'rotate-90' : ''}`}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
            </svg>
          </button>
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-medium text-foreground">{objectName}</h3>
            <span className="truncate text-xs text-muted-foreground block" title={object.className}>
              {object.className}
            </span>
            {classInfo?.memo && (
              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                {classInfo.memo}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Status badge and actions menu */}
      <div className="relative flex flex-col items-end gap-1 ml-2 shrink-0">
        <div className="flex items-center gap-1">
          {/* Documentation button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenDocs();
            }}
            className="rounded p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary"
            title="View documentation"
          >
            <BookOpenIcon className="h-5 w-5" />
          </button>
          {/* More actions menu */}
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="rounded p-1 text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
          </button>
        </div>
        <StatusBadge status={validation.status} />

        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowMenu(false)}
            />
            <div className="absolute right-0 top-full z-20 mt-1 w-40 rounded-md border border-border bg-card py-1 shadow-lg">
              <button
                onClick={() => {
                  onOpenDocs();
                  setShowMenu(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted"
              >
                <BookOpenIcon className="h-4 w-4" />
                View Docs
              </button>
              <button
                onClick={() => {
                  onDuplicate();
                  setShowMenu(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                </svg>
                Duplicate
              </button>
              <button
                onClick={() => {
                  onDelete();
                  setShowMenu(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-destructive hover:bg-destructive/10"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                </svg>
                Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Reference info section showing objects that reference or are referenced by this object
 */
function ReferenceInfo({
  referencingCount,
  referencedCount,
}: {
  referencingCount: number;
  referencedCount: number;
}) {
  if (referencingCount === 0 && referencedCount === 0) return null;

  return (
    <div className="border-t bg-muted px-4 py-2">
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        {referencingCount > 0 && (
          <span>
            Referenced by {referencingCount} object{referencingCount !== 1 ? 's' : ''}
          </span>
        )}
        {referencedCount > 0 && (
          <span>
            References {referencedCount} object{referencedCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Field group separator
 */
function FieldGroupSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-2">
      <div className="h-px flex-1 bg-border" />
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

/**
 * Object Editor Card Props
 */
interface ObjectEditorCardProps {
  object: IDFObject;
  selected?: boolean;
  onSelect?: (e: React.MouseEvent) => void;
}

/**
 * Object Editor Card Component
 */
export function ObjectEditorCard({
  object,
  selected = false,
  onSelect,
}: ObjectEditorCardProps) {
  // Start collapsed by default for better performance with many cards
  const [expanded, setExpanded] = useState(false);

  // Store state
  const document = useIDFStore((state) => state.document);
  const schema = useIDDStore((state) => state.schema);
  const isSchemaLoaded = useIDDStore((state) => state.isLoaded);

  // Documentation hook
  const { openDocsPanel } = useDocumentation();

  // Store actions
  const updateField = useIDFStore((state) => state.updateField);
  const duplicateObject = useIDFStore((state) => state.duplicateObject);
  const removeObject = useIDFStore((state) => state.removeObject);

  // Get class info from IDD
  const classInfo = useMemo(() => {
    if (!isSchemaLoaded) return undefined;
    return schema.objectTypes.get(object.className.toLowerCase());
  }, [schema, object.className, isSchemaLoaded]);

  // Calculate validation - always use full validation for consistent status display
  const validation = useMemo((): ObjectValidation => {
    return validateObject(object, classInfo, document, isSchemaLoaded ? schema : null);
  }, [object, classInfo, document, schema, isSchemaLoaded]);

  // Count references - only when expanded for performance
  const { referencingCount, referencedCount } = useMemo(() => {
    // Skip expensive reference counting when collapsed
    if (!expanded) return { referencingCount: 0, referencedCount: 0 };
    if (!document || !isSchemaLoaded) return { referencingCount: 0, referencedCount: 0 };

    const objectName = object.fields[0]?.value;
    if (!objectName) return { referencingCount: 0, referencedCount: 0 };

    const referencing = findReferencingObjects(objectName, document, schema);

    // Count referenced objects
    let refCount = 0;
    if (classInfo) {
      for (let i = 0; i < object.fields.length; i++) {
        const field = object.fields[i];
        const fieldInfo = classInfo.fields[i];
        if (fieldInfo?.objectList && field?.value) {
          refCount++;
        }
      }
    }

    return { referencingCount: referencing.length, referencedCount: refCount };
  }, [object, document, schema, classInfo, isSchemaLoaded, expanded]);

  // Get objects that can be referenced for each field
  const getReferencedObjects = useCallback(
    (fieldInfo: IDDField): IDFObject[] => {
      if (!fieldInfo.objectList || !document || !isSchemaLoaded) return [];

      const referencedTypes = schema.referenceLists.get(fieldInfo.objectList);
      if (!referencedTypes) return [];

      return document.objects.filter((obj) => {
        return (
          referencedTypes.has(obj.className) ||
          referencedTypes.has(obj.className.toLowerCase())
        );
      });
    },
    [document, schema, isSchemaLoaded]
  );

  // Handle field change
  const handleFieldChange = useCallback(
    (fieldIndex: number, value: string) => {
      updateField(object.id, fieldIndex, { value });
    },
    [object.id, updateField]
  );

  // Handle duplicate
  const handleDuplicate = useCallback(() => {
    duplicateObject(object.id);
  }, [object.id, duplicateObject]);

  // Handle delete
  const handleDelete = useCallback(() => {
    if (confirm(`Delete ${object.fields[0]?.value || object.className}?`)) {
      removeObject(object.id);
    }
  }, [object, removeObject]);

  // Handle open documentation
  const handleOpenDocs = useCallback(() => {
    openDocsPanel(object.className);
  }, [object.className, openDocsPanel]);

  // Build field list with proper field info
  const fields = useMemo(() => {
    const maxFields = Math.max(
      object.fields.length,
      classInfo?.fields.length ?? 0
    );
    const result: {
      field: IDFField | undefined;
      fieldInfo: IDDField | undefined;
      index: number;
      validation: FieldValidation;
    }[] = [];

    for (let i = 0; i < maxFields; i++) {
      result.push({
        field: object.fields[i],
        fieldInfo: classInfo?.fields[i],
        index: i,
        validation: validation.fields[i] ?? {
          status: 'valid',
          errors: [],
          warnings: [],
        },
      });
    }

    return result;
  }, [object.fields, classInfo?.fields, validation.fields]);

  // Separate required and optional fields
  const { requiredFields, optionalFields } = useMemo(() => {
    const required = fields.filter((f) => f.fieldInfo?.required);
    const optional = fields.filter((f) => !f.fieldInfo?.required);
    return { requiredFields: required, optionalFields: optional };
  }, [fields]);

  if (!classInfo) {
    return (
      <div
        className={`rounded-lg border bg-card shadow-sm ${
          selected ? 'ring-2 ring-primary' : ''
        }`}
        onClick={onSelect}
      >
        <div className="p-4 text-center text-muted-foreground">
          <p className="font-medium">{object.className}</p>
          <p className="text-sm">Schema not loaded or unknown object type</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border bg-card shadow-sm transition-shadow hover:shadow-md overflow-hidden ${
        selected ? 'ring-2 ring-primary' : ''
      }`}
      onClick={onSelect}
    >
      <CardHeader
        object={object}
        classInfo={classInfo}
        validation={validation}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        onOpenDocs={handleOpenDocs}
        expanded={expanded}
        onToggleExpand={() => setExpanded(!expanded)}
      />

      {expanded && (
        <div className="p-4 space-y-3">
          {/* Required fields */}
          {requiredFields.length > 0 && (
            <>
              <FieldGroupSeparator label="Required" />
              {requiredFields.map(({ field, fieldInfo, index, validation: fieldValidation }) =>
                fieldInfo ? (
                  <FieldEditor
                    key={index}
                    object={object}
                    field={field}
                    fieldInfo={fieldInfo}
                    fieldIndex={index}
                    validation={fieldValidation}
                    referencedObjects={getReferencedObjects(fieldInfo)}
                    onChange={(value) => handleFieldChange(index, value)}
                  />
                ) : null
              )}
            </>
          )}

          {/* Optional fields */}
          {optionalFields.length > 0 && (
            <>
              <FieldGroupSeparator label="Optional" />
              {optionalFields.map(({ field, fieldInfo, index, validation: fieldValidation }) =>
                fieldInfo ? (
                  <FieldEditor
                    key={index}
                    object={object}
                    field={field}
                    fieldInfo={fieldInfo}
                    fieldIndex={index}
                    validation={fieldValidation}
                    referencedObjects={getReferencedObjects(fieldInfo)}
                    onChange={(value) => handleFieldChange(index, value)}
                  />
                ) : null
              )}
            </>
          )}
        </div>
      )}

      <ReferenceInfo
        referencingCount={referencingCount}
        referencedCount={referencedCount}
      />
    </div>
  );
}

/**
 * Pagination controls
 */
function PaginationControls({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}: {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (perPage: number) => void;
}) {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border-t bg-card px-4 py-3">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>
          Showing {startItem}-{endItem} of {totalItems} objects
        </span>
        <span className="text-muted-foreground/50">|</span>
        <label className="flex items-center gap-1">
          <span>Per page:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            className="rounded border border-border bg-background px-2 py-1 text-sm text-foreground"
          >
            <option value={12}>12</option>
            <option value={24}>24</option>
            <option value={48}>48</option>
            <option value={96}>96</option>
          </select>
        </label>
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="rounded px-2 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          title="First page"
        >
          ««
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="rounded px-2 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          title="Previous page"
        >
          «
        </button>

        {/* Page numbers */}
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum: number;
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else if (currentPage <= 3) {
            pageNum = i + 1;
          } else if (currentPage >= totalPages - 2) {
            pageNum = totalPages - 4 + i;
          } else {
            pageNum = currentPage - 2 + i;
          }
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`rounded px-3 py-1 text-sm ${
                pageNum === currentPage
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {pageNum}
            </button>
          );
        })}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="rounded px-2 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          title="Next page"
        >
          »
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="rounded px-2 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          title="Last page"
        >
          »»
        </button>
      </div>
    </div>
  );
}

/**
 * Object Editor Cards Grid
 * Displays multiple object cards in a responsive grid layout with pagination
 */
export function ObjectEditorGrid({
  objects,
  selectedIds,
  onSelect,
}: {
  objects: IDFObject[];
  selectedIds: string[];
  onSelect: (id: string, multi: boolean) => void;
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);

  // Reset to page 1 when objects change significantly
  const objectsKey = objects.length;
  const prevObjectsKey = useRef(objectsKey);
  if (objectsKey !== prevObjectsKey.current) {
    prevObjectsKey.current = objectsKey;
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }

  const totalPages = Math.max(1, Math.ceil(objects.length / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedObjects = useMemo(() => {
    const start = (safeCurrentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return objects.slice(start, end);
  }, [objects, safeCurrentPage, itemsPerPage]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // Scroll to top of grid
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleItemsPerPageChange = useCallback((perPage: number) => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
  }, []);

  if (objects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <svg
          className="h-16 w-16 text-muted-foreground/50"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="mt-4 text-lg font-medium">No objects to display</p>
        <p className="text-sm">Select an object class or adjust filters</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Pagination header when many objects */}
      {objects.length > itemsPerPage && (
        <div className="mb-4">
          <PaginationControls
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            totalItems={objects.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      )}

      {/* Cards grid */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 min-w-0">
        {paginatedObjects.map((obj) => (
          <ObjectEditorCard
            key={obj.id}
            object={obj}
            selected={selectedIds.includes(obj.id)}
            onSelect={(e) => onSelect(obj.id, e.ctrlKey || e.metaKey)}
          />
        ))}
      </div>

      {/* Pagination footer when many objects */}
      {objects.length > itemsPerPage && (
        <div className="mt-4">
          <PaginationControls
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            totalItems={objects.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      )}
    </div>
  );
}
